import { Box } from "@mui/material";
import type { FreemiumPlanResponseDetailType } from "@trainwell/features/freemium-plans";
import { MuiTelInput, matchIsValidTel } from "mui-tel-input";
import { useEffect, useState } from "react";
import { useButtonStore } from "src/components/ButtonState";
import { ErrorMessage } from "src/components/ErrorMessage";
import { useErrorState } from "src/components/ErrorState";
import { FreeboardingSubtitle } from "src/components/FreeboardingSubtitle";
import { StyledTextField } from "src/components/FreeboardingTextField";
import { useUserState } from "src/components/UserState";
import { theme } from "src/theme/theme";
import { SuccessBadge } from "../components/FreeboardingBadge";
import { FreeboardingParagraph } from "../components/FreeboardingParagraph";
import { FreeboardingTitle } from "../components/FreeboardingTitle";
import TrainerPlanCardSmall from "./training-plans/TrainerPlanCardSmall";

/**
 * This component display intake form for the user to sign up after they have selected
 * the plan they want to try. If the email is valid, they can continue.
 */

interface IntakePageProps {
  chipText: string;
  titleText: string;
  bodyText: string;
  selectedPlan?: FreemiumPlanResponseDetailType;
}

const IntakePageComponent = ({
  chipText,
  titleText,
  bodyText,
  selectedPlan,
}: IntakePageProps) => {
  const setButtonEnabled = useButtonStore((state) => state.setButtonEnabled);

  const email = useUserState((state) => state.email);
  const setEmail = useUserState((state) => state.setEmail);

  const phoneNumber = useUserState((state) => state.phoneNumber);
  const setPhoneNumber = useUserState((state) => state.setPhoneNumber);
  const [rawPhoneNumber, setRawPhoneNumber] = useState(phoneNumber);
  const isPhoneNumberValid = import.meta.env.DEV
    ? (phoneNumber?.length ?? 0) > 4
    : matchIsValidTel(phoneNumber ?? "");

  const firstName = useUserState((state) => state.firstName);
  const setFirstName = useUserState((state) => state.setFirstName);
  const lastName = useUserState((state) => state.lastName);
  const setLastName = useUserState((state) => state.setLastName);
  const errorMessage = useErrorState((state) => state.errorMessage);

  useEffect(() => {
    // Email is valid if it contains an @ and a .
    const emailIsValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email || "");
    // First name is valid if it contains only letters.
    const firstNameIsValid = /[a-zA-Z]/.test(firstName || "");
    // Last name is valid if it contains only letters.
    const lastNameIsValid = /[a-zA-Z]/.test(lastName || "");

    setButtonEnabled(
      emailIsValid && isPhoneNumberValid && firstNameIsValid && lastNameIsValid,
    );
  }, [firstName, lastName, email, isPhoneNumberValid, setButtonEnabled]);

  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}
    >
      <SuccessBadge sx={{ marginBottom: theme.spacing(2) }} label={chipText} />
      {/* TODO(akrulec): Fix null state here. */}
      {selectedPlan && <TrainerPlanCardSmall plan={selectedPlan} />}
      <FreeboardingTitle>{titleText}</FreeboardingTitle>
      <FreeboardingParagraph>{bodyText}</FreeboardingParagraph>
      <Box
        display="flex"
        flexDirection="column"
        alignItems="start"
        width="100%"
        marginBottom={theme.spacing(4)}
      >
        <FreeboardingSubtitle>First Name</FreeboardingSubtitle>
        <StyledTextField
          value={firstName || ""}
          autoComplete="given-name"
          onChange={(e) => {
            setFirstName(e.target.value);
          }}
        />
        <FreeboardingSubtitle>Last Name</FreeboardingSubtitle>
        <StyledTextField
          value={lastName || ""}
          autoComplete="family-name"
          onChange={(e) => {
            setLastName(e.target.value);
          }}
        />
        <FreeboardingSubtitle>Email</FreeboardingSubtitle>
        <StyledTextField
          type="email"
          autoCapitalize="none"
          autoComplete="email"
          value={email || ""}
          onChange={(e) => {
            setEmail(e.target.value);
          }}
        />
        <FreeboardingSubtitle>Phone Number</FreeboardingSubtitle>
        <MuiTelInput
          defaultCountry="US"
          autoComplete="telephone"
          fullWidth
          value={rawPhoneNumber}
          onChange={(newValue) => {
            const formattedPhoneNumber = "+" + newValue.replace(/\D/g, "");
            setRawPhoneNumber(newValue);
            setPhoneNumber(formattedPhoneNumber);
          }}
        />
        {/* I would like to display an error, if there is one to display */}
        {errorMessage && <ErrorMessage message={errorMessage} />}
      </Box>
    </Box>
  );
};

export default IntakePageComponent;
